import { useMemo, useState } from "react";

import {
  ActionIcon,
  Badge,
  Divider,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Tabs,
  Text,
  Group,
  Card,
  Grid,
  Title,
  Select,
  Button,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import { Eye, Activity, History, Filter, X } from "tabler-icons-react";
import ParentLoginStatsChart from "../../components/ParentLoginStatsChart";
import {
  useGetAllElevesByParentQuery,
  useGetStudentActivityQuery,
} from "../../redux/features/eleveApi";
import { useGetParentActivitiesStatsQuery } from "../../redux/features/parentApi";

function DetailsParent({ parent }) {
  const [opened, { open, close }] = useDisclosure(false);
  const [activeTab, setActiveTab] = useState("enfants");

  // États pour les filtres des statistiques
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const { data: students, isLoading } = useGetAllElevesByParentQuery(
    parent.id,
    { skip: !parent.id },
  );

  // Récupérer les activités des enfants de ce parent
  const { data: activityData, isLoading: activityLoading } =
    useGetStudentActivityQuery(
      { parentId: parent.id },
      { skip: !parent.id || !opened },
    );

  // Récupérer les statistiques d'activités avec filtres
  const { data: activitiesStats, isLoading: statsLoading } =
    useGetParentActivitiesStatsQuery(
      {
        parentId: parent.id,
        startDate: startDate ? startDate.toISOString().split('T')[0] : undefined,
        endDate: endDate ? endDate.toISOString().split('T')[0] : undefined,
        studentId: selectedStudent,
      },
      { skip: !parent.id || !opened },
    );

  // Réinitialiser les filtres
  const handleResetFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedStudent(null);
  };

  // Options pour le select des élèves
  const studentOptions = useMemo(() => {
    if (!students?.data) return [];
    return students.data.map((student) => ({
      value: student.id.toString(),
      label: `${student.firstName} ${student.lastName}`,
    }));
  }, [students]);

  // Mapper la dernière activité par studentId
  const lastActivityByStudent = useMemo(() => {
    if (!activityData?.data) return {};
    const map = {};
    // Les données sont triées par date décroissante, on prend la première par élève
    activityData.data.forEach((activity) => {
      const studentId = activity.student?.id;
      if (studentId && !map[studentId]) {
        map[studentId] = activity;
      }
    });
    return map;
  }, [activityData]);

  const formatDate = (activity) => {
    if (!activity?.date_diagnostic) return "";
    const options = {
      day: "2-digit",
      month: "short",
      year: "numeric",
    };

    // Afficher l'heure uniquement si created_at est fiable :
    // on vérifie que created_at tombe le même jour que date_diagnostic
    // (les anciennes tentatives ont un created_at faux = date de migration)
    if (activity?.created_at) {
      const createdDate = new Date(activity.created_at);
      const diagDate = new Date(activity.date_diagnostic + "T00:00:00");
      const sameDay =
        createdDate.getFullYear() === diagDate.getFullYear() &&
        createdDate.getMonth() === diagDate.getMonth() &&
        createdDate.getDate() === diagDate.getDate();

      if (sameDay) {
        // C'est une vraie tentative récente, on affiche date + heure
        return createdDate.toLocaleDateString("fr-FR", {
          ...options,
          hour: "2-digit",
          minute: "2-digit",
        });
      }
    }

    // Ancienne tentative ou pas de created_at : date seule
    const date = new Date(activity.date_diagnostic + "T00:00:00");
    return date.toLocaleDateString("fr-FR", options);
  };

  const StatusBadge = ({ status }) => {
    if (status === "VALIDE") {
      return (
        <Badge color="green" size="sm" variant="filled">
          Validé
        </Badge>
      );
    }
    if (status === "ENCOURS") {
      return (
        <Badge color="orange" size="sm" variant="filled">
          En cours
        </Badge>
      );
    }
    return (
      <Badge color="gray" size="sm" variant="filled">
        {status}
      </Badge>
    );
  };

  const StudentByParent = () => {
    const rows = students?.data.map((data) => {
      const activity = lastActivityByStudent[data.id];

      return (
        <tr key={data.id}>
          <td>
            {data?.firstName} {data?.lastName}
          </td>
          <td>{data?.classe?.name}</td>
          <td>
            {activityLoading ? (
              <Loader size="xs" />
            ) : activity ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Text size="sm" weight={500}>
                    {activity.matiere}
                  </Text>
                  <StatusBadge status={activity.status} />
                </div>
                <Text size="xs" color="dimmed">
                  {formatDate(activity)}
                  {activity.score_max > 0 &&
                    ` — ${activity.score_total}/${activity.score_max} pts`}
                </Text>
              </div>
            ) : (
              <Text size="sm" color="dimmed" italic>
                Aucune activité
              </Text>
            )}
          </td>
        </tr>
      );
    });

    return (
      <div>
        <ScrollArea>
          <Table
            horizontalSpacing="md"
            verticalSpacing="xs"
            miw={124}
            sx={{ tableLayout: "fixed" }}
            bg={"#fff"}
          >
            <thead>
              <tr>
                <th>Elève</th>
                <th>Classe</th>
                <th>Dernière activité</th>
              </tr>
            </thead>
            <tbody>{rows}</tbody>
          </Table>
        </ScrollArea>
      </div>
    );
  };

  const ActivitiesStatsSection = () => {
    if (statsLoading) {
      return (
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <Loader />
        </div>
      );
    }

    if (!activitiesStats?.data) {
      return (
        <Text color="dimmed" italic>
          Aucune donnée disponible
        </Text>
      );
    }

    const stats = activitiesStats.data;
    const hasFilters = startDate || endDate || selectedStudent;

    return (
      <Stack spacing="lg">
        {/* Section des filtres - Sticky */}
        <Card
          shadow="sm"
          p="md"
          radius="md"
          withBorder
          style={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: "white",
          }}
        >
          <Group position="apart" mb="md">
            <Title order={5}>
              <Filter size={18} style={{ marginRight: 8, verticalAlign: "middle" }} />
              Filtres
            </Title>
            {hasFilters && (
              <Button
                size="xs"
                variant="subtle"
                color="gray"
                leftIcon={<X size={14} />}
                onClick={handleResetFilters}
              >
                Réinitialiser
              </Button>
            )}
          </Group>
          <Grid>
            <Grid.Col span={4}>
              <DatePickerInput
                label="Date de début"
                placeholder="Sélectionner"
                value={startDate}
                onChange={setStartDate}
                clearable
                size="sm"
                dropdownPosition="bottom"
                withinPortal
                zIndex={2000}
                popoverProps={{
                  withinPortal: true,
                  zIndex: 2000,
                }}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <DatePickerInput
                label="Date de fin"
                placeholder="Sélectionner"
                value={endDate}
                onChange={setEndDate}
                clearable
                size="sm"
                dropdownPosition="bottom"
                withinPortal
                zIndex={2000}
                popoverProps={{
                  withinPortal: true,
                  zIndex: 2000,
                }}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <Select
                label="Élève"
                placeholder="Tous les élèves"
                data={studentOptions}
                value={selectedStudent}
                onChange={setSelectedStudent}
                clearable
                size="sm"
                dropdownPosition="bottom"
                withinPortal
                zIndex={2000}
                maxDropdownHeight={200}
              />
            </Grid.Col>
          </Grid>
          {hasFilters && (
            <Text size="xs" color="dimmed" mt="xs">
              {startDate && `Du ${startDate.toLocaleDateString("fr-FR")}`}
              {endDate && ` au ${endDate.toLocaleDateString("fr-FR")}`}
              {selectedStudent && ` • Élève: ${studentOptions.find(s => s.value === selectedStudent)?.label}`}
            </Text>
          )}
        </Card>

        {/* Résumé global */}
        <Card shadow="sm" p="md" radius="md" withBorder>
          <Title order={4} mb="md">
            Résumé global
          </Title>
          <Grid>
            <Grid.Col span={6}>
              <Text size="sm" color="dimmed">
                Total activités
              </Text>
              <Text size="xl" weight={700} color="blue">
                {stats.total_activities}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text size="sm" color="dimmed">
                Crédits dépensés
              </Text>
              <Text size="xl" weight={700} color="orange">
                {stats.total_credits_spent}
              </Text>
            </Grid.Col>
          </Grid>
        </Card>

        {/* Activités par type */}
        <Card shadow="sm" p="md" radius="md" withBorder>
          <Title order={4} mb="md">
            Activités par type
          </Title>
          <Stack spacing="sm">
            {Object.entries(stats.activities_by_type || {}).map(
              ([type, data]) => (
                <Group key={type} position="apart" p="xs" style={{ borderBottom: "1px solid #e9ecef" }}>
                  <div>
                    <Text weight={500}>{type}</Text>
                    <Text size="xs" color="dimmed">
                      {data.credit_per_use} crédits/utilisation
                    </Text>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <Text weight={600} color="blue">
                      {data.count} fois
                    </Text>
                    <Text size="xs" color="orange">
                      {data.credits_spent} crédits
                    </Text>
                  </div>
                </Group>
              ),
            )}
          </Stack>
        </Card>

        {/* Activités par élève */}
        {stats.activities_by_student && stats.activities_by_student.length > 0 && (
          <Card shadow="sm" p="md" radius="md" withBorder>
            <Title order={4} mb="md">
              Activités par élève
            </Title>
            <Stack spacing="md">
              {stats.activities_by_student.map((studentData) => (
                <Card key={studentData.student_id} p="sm" withBorder>
                  <Group position="apart" mb="xs">
                    <Text weight={600}>{studentData.student_name}</Text>
                    <Badge color="blue" variant="filled">
                      {studentData.total_activities} activités
                    </Badge>
                  </Group>
                  <Text size="sm" color="dimmed" mb="xs">
                    {studentData.total_credits_spent} crédits dépensés
                  </Text>
                  <Divider my="xs" />
                  <Stack spacing="xs">
                    {Object.entries(studentData.activities_by_type || {}).map(
                      ([type, data]) => (
                        <Group key={type} position="apart">
                          <Text size="sm">{type}</Text>
                          <Group spacing="xs">
                            <Badge size="sm" variant="light">
                              {data.count}x
                            </Badge>
                            <Text size="sm" color="orange">
                              {data.credits_spent} crédits
                            </Text>
                          </Group>
                        </Group>
                      ),
                    )}
                  </Stack>
                </Card>
              ))}
            </Stack>
          </Card>
        )}
      </Stack>
    );
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={close}
        withCloseButton={true}
        size="xl"
        title={`Détails de : ${parent.fullName}`}
      >
        <Tabs value={activeTab} onTabChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="enfants" icon={<Eye size={14} />}>
              Les enfants
            </Tabs.Tab>
            <Tabs.Tab value="stats" icon={<Activity size={14} />}>
              Statistiques des activités
            </Tabs.Tab>
            <Tabs.Tab value="connexions" icon={<History size={14} />}>
              Historique de connexion
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="enfants" pt="md">
            {isLoading ? (
              <Loader />
            ) : students?.data?.length === 0 ? (
              <Text color="dimmed" italic>
                Pas d'enfant
              </Text>
            ) : (
              <StudentByParent />
            )}
          </Tabs.Panel>

          <Tabs.Panel value="stats" pt="md">
            <ActivitiesStatsSection />
          </Tabs.Panel>

          <Tabs.Panel value="connexions" pt="md">
            <ParentLoginStatsChart
              parentId={parent.id}
              parentName={parent.fullName}
            />
          </Tabs.Panel>
        </Tabs>
      </Modal>

      <ActionIcon
        onClick={open}
        color="blue"
        size="md"
        variant="light"
        title="Voir les détails"
      >
        <Eye size={18} />
      </ActionIcon>
    </>
  );
}
export default DetailsParent;
